/**
 * One codebase, three surfaces: iOS, Android, and the web build that carries desktop.
 *
 * The split is by WIDTH, never by platform — a phone-sized window on a desktop behaves like a
 * phone, and a tablet gets the wide layout. This is the only file that chooses between the side
 * rail and the bottom bar; screens rearrange their own pieces and nothing below here decides
 * which shell it is in.
 *
 * Before the shell there is a gate: without a pairing key there is no app, only the link that
 * makes one. That is the whole auth model and it did not change in the migration.
 */
import { BottomNav, Brand, NarrowHeader, SideRail, ThemeToggle } from '@/components/nav';
import { COLUMN_CLASS } from '@/components/panel';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { api } from '@/api';
import { registerServiceWorker } from '@/push';
import { boot, choose, openPanel, pairWithKey } from '@/session';
import { DatesScreen } from '@/screens/Dates';
import { GratitudeScreen } from '@/screens/Gratitude';
import { HomeScreen } from '@/screens/Home';
import { ListScreen } from '@/screens/List';
import { MissionsScreen } from '@/screens/Missions';
import { PulseScreen } from '@/screens/Pulse';
import { ScoreScreen } from '@/screens/Score';
import { SignalsScreen } from '@/screens/Signals';
import { SparkScreen } from '@/screens/Spark';
import { SyncScreen } from '@/screens/Sync';
import { PreferenceSheet } from '@/screens/Preferences';
import { SignalsWalkthrough } from '@/screens/Walkthrough';
import { WrappedScreen } from '@/screens/Wrapped';
import { fontMap } from '@/lib/fonts';
import { lookVars } from '@/lib/looks';
import { useStore, type Identity } from '@/store';
import { useLayout } from '@/useLayout';
// Per-weight subpaths, not the package index. The index re-exports all nine weights of each
// family, and importing it puts eighteen TTFs (~900KB) into the bundle to use eight of them.
import { Figtree_400Regular } from '@expo-google-fonts/figtree/400Regular';
import { Figtree_500Medium } from '@expo-google-fonts/figtree/500Medium';
import { Figtree_600SemiBold } from '@expo-google-fonts/figtree/600SemiBold';
import { Figtree_700Bold } from '@expo-google-fonts/figtree/700Bold';
import { Fraunces_400Regular } from '@expo-google-fonts/fraunces/400Regular';
import { Fraunces_500Medium } from '@expo-google-fonts/fraunces/500Medium';
import { Fraunces_600SemiBold } from '@expo-google-fonts/fraunces/600SemiBold';
import { Fraunces_700Bold } from '@expo-google-fonts/fraunces/700Bold';
import { Fraunces_400Regular_Italic } from '@expo-google-fonts/fraunces/400Regular_Italic';
import { Fraunces_500Medium_Italic } from '@expo-google-fonts/fraunces/500Medium_Italic';
import { Fraunces_600SemiBold_Italic } from '@expo-google-fonts/fraunces/600SemiBold_Italic';
import { Fraunces_700Bold_Italic } from '@expo-google-fonts/fraunces/700Bold_Italic';
import { useFonts } from 'expo-font';
import * as Notifications from 'expo-notifications';
import { colorScheme, vars } from 'nativewind';
import * as React from 'react';
import { Platform, StatusBar, useColorScheme, View } from 'react-native';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';

/**
 * Every family/weight pair the app can ask for.
 *
 * All four weights of both faces, because a named font is a loaded font: the type scale sets
 * `font-semibold` in a dozen places, and a 600 that was never loaded renders as the system font
 * on Android with nothing to say it happened. `src/lib/fonts.ts` owns the naming convention.
 *
 * Fraunces is the display face and it comes with a matching set of italics, which are registered
 * under `Fraunces-Italic` rather than left to Android's own synthesised slant.
 */
const FONTS = fontMap({
  display: {
    400: Fraunces_400Regular,
    500: Fraunces_500Medium,
    600: Fraunces_600SemiBold,
    700: Fraunces_700Bold,
  },
  displayItalic: {
    400: Fraunces_400Regular_Italic,
    500: Fraunces_500Medium_Italic,
    600: Fraunces_600SemiBold_Italic,
    700: Fraunces_700Bold_Italic,
  },
  sans: {
    400: Figtree_400Regular,
    500: Figtree_500Medium,
    600: Figtree_600SemiBold,
    700: Figtree_700Bold,
  },
});

export default function App() {
  const systemTheme = useColorScheme();
  const { theme, toggleTheme, setOnline } = useStore();
  // Nothing renders until they are in. A first paint in Segoe UI that reflows into Fraunces a beat
  // later is worse than a beat of the themed ground colour, which is already painted by index.html.
  const [fontsLoaded, fontError] = useFonts(FONTS);

  // The store owns the theme so the toggle works, but the system preference decides the first
  // paint rather than a hardcoded default.
  React.useEffect(() => {
    if (systemTheme && systemTheme !== theme) toggleTheme();
    // Only on the first read of the system preference.
  }, [systemTheme]);

  // NativeWind is told, not asked: the store stays the single owner and this hands its answer to
  // the styling layer, which flips the `dark` class and with it every variable in global.css.
  React.useEffect(() => {
    colorScheme.set(theme);
  }, [theme]);

  React.useEffect(() => {
    api.onOnlineChange(setOnline);
    registerServiceWorker();
    void boot();

    if (Platform.OS === 'web') return;
    // A tapped native notification carries the module id the server put in its data payload —
    // the same id the web service worker turns into ?panel=.
    const sub = Notifications.addNotificationResponseReceivedListener((response) => {
      const module = response.notification.request.content.data?.module;
      if (typeof module === 'string') openPanel(module);
    });
    return () => sub.remove();
  }, [setOnline]);

  // A font that fails to load is a reason to ship the app in the fallback stack, not to show a
  // blank screen forever — so an error lets it through rather than holding it.
  if (!fontsLoaded && !fontError) return <View className="bg-background flex-1" />;

  return (
    <SafeAreaProvider>
      <Shell />
    </SafeAreaProvider>
  );
}

function Shell() {
  const { wide } = useLayout();
  const { screen, go, badges, error, dismissError, theme, toggleTheme, session, me, online, overlay } =
    useStore();
  // A plain View with inset padding, not RN's SafeAreaView: NativeWind does not style SafeAreaView
  // on native, which drops both the themed background and the root flex-1 the whole layout hangs on.
  const insets = useSafeAreaInsets();

  const gated = session !== 'ready';

  /**
   * THE LOOK, on native.
   *
   * The web switches looks with a `data-look` attribute on `<html>` and lets the stylesheet do the
   * rest. A phone has no attribute and no stylesheet, so the second look's variables have to be
   * handed to the tree as a NativeWind `vars()` table on this View — every token under it resolves
   * against these, exactly as a CSS block would. Bouquet gets `null`, because bouquet IS the bare
   * root: no table, no override, nothing to keep in step.
   *
   * The values come from `src/lib/looks.ts`, which is also what `gen-theme.mjs` emits the two
   * `[data-look='tuxedo']` CSS blocks from — so the web and native renderings cannot drift.
   *
   * Memoised on the two things that decide it, because a fresh object here would be a new style
   * on every render of the whole app.
   */
  const look = useStore((s) => s.look);
  const lookStyle = React.useMemo(() => {
    const table = lookVars(look, theme === 'dark' ? 'dark' : 'light');
    return table ? vars(table) : null;
  }, [look, theme]);

  return (
    <View
      /**
       * `fit-shell` is the hook the bouquet look uses to get OUT of the way: its ground is a
       * colour with a wash over it, painted on `body`, and an opaque shell here would cover it.
       * On tuxedo the class matches nothing and `bg-background` is the whole ground, correctly.
       */
      className="fit-shell bg-background flex-1"
      style={[
        { paddingTop: insets.top, paddingBottom: insets.bottom, paddingLeft: insets.left, paddingRight: insets.right },
        lookStyle,
      ]}>
      <StatusBar barStyle={theme === 'dark' ? 'light-content' : 'dark-content'} />

      <View className={cn('flex-1', wide && !gated ? 'flex-row' : 'flex-col')}>
        {wide && !gated ? (
          <SideRail screen={screen} onGo={go} badges={badges} theme={theme} onToggleTheme={toggleTheme} name={me?.name} />
        ) : null}

        <View className={cn('min-w-0 flex-1 gap-3', wide ? 'p-6' : 'p-4')}>
          {wide && !gated ? null : <NarrowHeader theme={theme} onToggleTheme={toggleTheme} name={gated ? undefined : me?.name} />}

          {/* THE SAME COLUMN THE PANEL IS IN. An alert stretched to the window while the cards it
              is warning about stop at 880 is the banner claiming to be about something wider than
              the app. `COLUMN_CLASS` is the panel's own, so the two can never drift. */}
          {!online ? (
            <View className={wide ? COLUMN_CLASS : undefined}>
              <Alert icon="◌">
                <AlertTitle>Reconnecting</AlertTitle>
                <AlertDescription>
                  The server is not answering. Nothing you do is lost — it just cannot be sent yet.
                </AlertDescription>
              </Alert>
            </View>
          ) : null}

          {error ? (
            <View className={wide ? COLUMN_CLASS : undefined}>
              <Alert variant="destructive" icon="▲">
                <AlertTitle>That did not go through</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
                <Button variant="ghost" size="sm" className="self-start" onPress={dismissError}>
                  <Text>Dismiss</Text>
                </Button>
              </Alert>
            </View>
          ) : null}

          <View className="min-h-0 flex-1">
            {gated ? <Gate /> : overlay ? <Overlay which={overlay} /> : <CurrentScreen />}
          </View>
        </View>

        {wide || gated ? null : <BottomNav screen={screen} onGo={go} badges={badges} />}
      </View>
    </View>
  );
}

/**
 * The two full-page views that are not tabs: the preference sheet and the Signals walkthrough.
 *
 * They live over the current screen rather than in the registry because neither is somewhere you
 * navigate TO — the sheet is opened from Home or from the Signals box, and the walkthrough opens
 * itself, once. Giving either one a tab would be advertising it forever for a thing you do twice.
 */
function Overlay({ which }: { which: 'prefs' | 'walkthrough' }) {
  return which === 'prefs' ? <PreferenceSheet /> : <SignalsWalkthrough />;
}

function CurrentScreen() {
  const screen = useStore((s) => s.screen);
  switch (screen) {
    case 'home':
      return <HomeScreen />;
    case 'missions':
      return <MissionsScreen />;
    case 'signals':
      return <SignalsScreen />;
    case 'list':
      return <ListScreen />;
    case 'gratitude':
      return <GratitudeScreen />;
    case 'sync':
      return <SyncScreen />;
    case 'dates':
      return <DatesScreen />;
    case 'score':
      return <ScoreScreen />;
    case 'wrapped':
      return <WrappedScreen />;
    // Spark is finished and is not in MODULES: no tab reaches it, and no invite is created for
    // it. The screen stays wired up so turning it back on is one line in the registry.
    case 'spark':
      return <SparkScreen />;
    case 'pulse':
      return <PulseScreen />;
    default:
      return null;
  }
}

/** Everything before the app: booting, choosing an identity, or having no pairing link at all. */
function Gate() {
  const { session, identities } = useStore();

  if (session === 'boot') {
    return (
      <View className="flex-1 items-center justify-center gap-3">
        <Brand />
        <Text variant="small" className="text-muted-foreground">
          Opening…
        </Text>
      </View>
    );
  }

  if (session === 'pick') {
    return (
      <View className="flex-1 items-center justify-center gap-3">
        <Card className="w-full max-w-[420px]">
          <CardContent>
            <CardTitle>Who’s playing?</CardTitle>
            {/**
             * WHAT THE APP IS, THEN WHY THIS SCREEN EXISTS — in that order, and with no environment
             * variable named on it.
             *
             * This is the first screen a phone opens on, and it was one sentence of server
             * configuration: a person handed the link had nothing on it telling them what they had
             * been handed. "Turn off HUNSY_DEBUG_PICKER" is also a fact about somebody else's
             * server, which is not a thing to put in front of a partner.
             */}
            <CardDescription>
              Hunsy is a game for two. Say the honest thing here; it is never quoted to them.
            </CardDescription>
            <Text variant="note">
              The debug picker is on for this server, so no pairing link is needed. You are asked once per
              device; Switch on Home changes who you are playing as.
            </Text>
            {identities.map((identity: Identity) => (
              <Button key={identity.partner} className="self-stretch" onPress={() => void choose(identity)}>
                <Text>{identity.name}</Text>
              </Button>
            ))}
          </CardContent>
        </Card>
      </View>
    );
  }

  return <Unpaired badKey={session === 'badkey'} />;
}

/**
 * No key on this device. The link is the one-tap route; typing it is the fallback for a phone that
 * was never handed one — a build opened from a QR code in Expo Go, most obviously.
 */
function Unpaired({ badKey }: { badKey: boolean }) {
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  return (
    <View className="flex-1 items-center justify-center gap-3">
      <Card className="w-full max-w-[420px]">
        <CardContent>
          <CardTitle>{badKey ? 'That key is no longer valid' : 'You need your pairing key'}</CardTitle>
          <CardDescription>
            {badKey
              ? 'The key on this device is not one the server recognises any more. Open your ?key= link again, or paste a fresh key.'
              : 'Hunsy has no accounts. Each of you gets a one-tap link — open yours on this device and it pairs itself.'}
          </CardDescription>

          <Text variant="small" className="text-muted-foreground">
            No link to hand? Paste the key itself.
          </Text>
          <Input
            value={draft}
            onChangeText={(v) => {
              setDraft(v);
              setFailed(false);
            }}
            placeholder="Pairing key"
            autoCapitalize="none"
            autoCorrect={false}
            secureTextEntry
            aria-invalid={failed}
            className={failed ? 'border-destructive' : undefined}
          />
          {failed ? (
            <Text variant="small" className="text-destructive">
              The server did not recognise that key.
            </Text>
          ) : null}
          <Button
            className="self-start"
            disabled={!draft.trim() || busy}
            onPress={async () => {
              setBusy(true);
              const ok = await pairWithKey(draft).catch(() => false);
              setBusy(false);
              if (ok) setDraft('');
              else setFailed(true);
            }}>
            <Text>Pair this device</Text>
          </Button>
        </CardContent>
      </Card>
    </View>
  );
}
